import { h, Component } from "preact";
import { Router } from "preact-router";
import { Provider } from "react-redux";

import AsyncRoute from "preact-async-route";
import Footer from "./components/Footer";
import Page from "./components/Page";
import Content from "./components/Content";
import config from "./config";
import "./utils/PolyFill.js";

import create from "./state/store";

import Event from "./routes/event/Event";
import Organiser from "./routes/organiser/Organiser";
import NoEvent from "./routes/noEvent/NoEvent";
import Tickets from "./routes/tickets/Tickets";
import EventListings from "./routes/widgets/EventListings";
import AdditionalQuestionsPreview from "./routes/widgets/AdditionalQuestionsPreview";
import AssignTickets from "./routes/conferences/AssignTickets";
import Details from "./routes/details/Details";
import Payment from "./routes/payment/Payment";
import Complete from "./routes/complete/Complete";
import AfterCheckout from "./routes/afterCheckout/AfterCheckout";
import Merchandise from "./routes/merchandise/Merchandise";
import Tour from "./routes/tours/Tour";
import GiftCardPage from "./routes/giftCards/GiftCardPage";
import GiftCardEventPage from "./routes/giftCards/GiftCardEventPage";
import GlobalOrderComplete from "./routes/complete/GlobalOrderComplete";
import PublicOrderDetails from "./routes/public/editOrder/PublicOrderDetails";
import EditTicket from "./routes/public/editTicket/EditTicket";

import(/* webpackPrefetch: true */ "./routes/contact/Contact");
import(/* webpackPrefetch: true */ "./routes/accessibility/Accessibility");
import(/* webpackPrefetch: true */ "./routes/waitlistPass/WaitlistPass");

import { parseQueryString } from "./utils/Format";
import cssVars from "./utils/css-vars-ponyfill";
import GoogleTagManagerService from "./services/tracking/GoogleTagManagerService";

const PageLoading = () => {
  return (
    <Page>
      <Content style={{ padding: 30 }}>
        <div class="loading loading-lg" />
      </Content>
    </Page>
  );
};

export default class App extends Component {
  constructor() {
    super();
    this.getLoadingComponent = this.getLoadingComponent.bind(this);

    this.getSeatingMap = this.getSeatingMap.bind(this);
    this.getContact = this.getContact.bind(this);
    this.getAccessibility = this.getAccessibility.bind(this);
    this.getWaitlistPass = this.getWaitlistPass.bind(this);
    this.handleRoute = this.handleRoute.bind(this);
  }

  handleRoute(evt) {
    if (typeof window !== "undefined") {
      window.scroll(0, 0);
      const canonicalPath = evt.url.split("?")[0];
      const ticketingUrl = config("TICKETING_URL");
      try {
        const canonicalElement = document.getElementById("canonical-url");
        if (canonicalElement) {
          canonicalElement.href = `${ticketingUrl}${canonicalPath}`;
        }
      } catch (err) {}

      if (this.isWidget) {
        const appDom = document.getElementById("app");
        const scrollHeight = appDom ? appDom.scrollHeight : null;
        window.top.postMessage(
          { scrollHeight: scrollHeight, pageChange: true },
          "*"
        );
      }
    }
  }

  componentWillMount() {
    this.isWidget = false;
    const isBrowser = typeof window !== "undefined";
    if (isBrowser && window.self !== window.top) {
      this.isWidget = true;
    }
    cssVars();
  }
  componentDidMount() {
    if (typeof window !== "undefined" && this.isWidget) {
      let appDom = document.getElementById("app");
      setInterval(function() {
        appDom = appDom ? appDom : document.getElementById("app");
        if (appDom) {
          window.top.postMessage(
            {
              scrollHeight: appDom.scrollHeight,
              location: window.location.href
            },
            "*"
          );
        }
      }, 100);
    }
  }

  getSeatingMap(url, cb, props) {
    const componentOrPromise = import("./routes/seatingMap/SeatingMap");
    return this.processComponent(cb, componentOrPromise);
  }

  getContact(url, cb, props) {
    const componentOrPromise = import("./routes/contact/Contact");
    return this.processComponent(cb, componentOrPromise);
  }

  getAccessibility(url, cb, props) {
    const componentOrPromise = import("./routes/accessibility/Accessibility");
    return this.processComponent(cb, componentOrPromise);
  }

  getWaitlistPass(url, cb, props) {
    const componentOrPromise = import("./routes/waitlistPass/WaitlistPass");
    return this.processComponent(cb, componentOrPromise);
  }

  getPublicSeatingMap = (url, cb, props) => {
    const componentOrPromise = import("./routes/public/seatingMap/PublicSeatingMap");
    return this.processComponent(cb, componentOrPromise);
  };

  processComponent = (cb, componentOrPromise) => {
    if (componentOrPromise.then) {
      // for client
      return componentOrPromise.then(module => module.default);
    } else if (componentOrPromise.default) {
      // for node
      cb({ component: componentOrPromise.default });
    }
  };

  getLoadingComponent() {
    return <PageLoading />;
  }

  render({
    url = null,
    eventData = null,
    orderData = null,
    merchData = null,
    tourData = null
  }) {
    let providedOrderData = {};
    let eventDateIds;
    if (typeof window !== "undefined") {
      url =
        window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.url
          ? window.__PRELOADED_STATE__.url
          : url;
      eventData =
        window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.eventData
          ? window.__PRELOADED_STATE__.eventData
          : eventData;
      orderData =
        window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.orderData
          ? window.__PRELOADED_STATE__.orderData
          : orderData;
      merchData =
        window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.merchData
          ? window.__PRELOADED_STATE__.merchData
          : merchData;
      tourData =
        window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.tourData
          ? window.__PRELOADED_STATE__.tourData
          : tourData;
      delete window.__PRELOADED_STATE__;
      // load the params passed in
      const params = parseQueryString(location.search.replace("?", ""));
      // save pre set order data
      const allowedOrderData = ["firstName", "lastName", "mobile", "email"];
      allowedOrderData.forEach(key => {
        if (params[key.toLowerCase()]) {
          providedOrderData[key] = params[key.toLowerCase()];
        }
      });

      // track affiliateCode
      const affiliateCode = params.c;
      if (affiliateCode && eventData && eventData.affiliateCodes) {
        // check ref
        const affiliateCodes = eventData.affiliateCodes.filter(
          affiliate => affiliate.code === affiliateCode
        );
        const existingCode = affiliateCodes.length ? affiliateCodes[0] : false;
        // save ref link for affiliate tracking
        if (existingCode) {
          try {
            localStorage.setItem(
              `${eventData.slug}-affiliate`,
              existingCode.code
            );
          } catch (err) {}
        }
      }
      eventDateIds = params.eventDateIds;
      // track affiliateCode
      const fb_oea = params.fb_oea;
      if (fb_oea) {
        window.fb_oea = fb_oea;
      }
      const { token, manualOrder, manualOrderSettings } = params;
      if (token && manualOrder) {
        window.manualOrderToken = token;
        window.isManualOrder = manualOrder === "true";
        window.manualOrderSettings = manualOrderSettings;
      }
    }
    const store = create(
      eventData,
      orderData,
      providedOrderData,
      eventDateIds,
      merchData,
      tourData
    );

    return (
      <Provider store={store}>
        <main id="app" class={this.isWidget ? "widget" : ""}>
          <Router url={url} onChange={this.handleRoute}>
            <NoEvent path="/" />
            <Event path="/:eventSlug" />
            <Organiser path="/organisers/:organiserId" />
            <EventListings path="/event-listings" />
            <AdditionalQuestionsPreview path="/:eventSlug/additional-questions-preview" />
            <Tour path="/tours/:slug/:tourGroupId?" />
            <Tickets path="/:eventSlug/tickets" />
            <AssignTickets path="/:eventSlug/assign/:orderId" />
            <Merchandise path="/:eventSlug/add-ons/:orderId" />
            <Details path="/:eventSlug/details/:orderId" />
            <Details path="/gift-cards/:eventSlug/details/:orderId" />
            <Payment path="/:eventSlug/payment/:orderId" />
            <Payment path="/gift-cards/:eventSlug/payment/:orderId" />
            <Complete path="/:eventSlug/complete/:orderId" />
            <GlobalOrderComplete path="/gift-cards/:slug/complete/:orderId" />
            <AfterCheckout path="/:eventSlug/afterCheckout/:orderId" />
            <GiftCardPage path="/gift-cards/:giftCardSlug" />
            <GiftCardEventPage path="/gift-cards/:giftCardSlug/event-list/:giftCardTypeId" />
            {/** ============= Public after order routes ============== */}
            <PublicOrderDetails path="/public/edit-order/:orderId/:eventId" />
            <EditTicket path="/public/edit-tickets/:orderId/:eventId/:attendeeProfileId" />
            <AsyncRoute
              loading={this.getLoadingComponent}
              getComponent={this.getPublicSeatingMap}
              path="/public/view-seating-map/:orderId/:eventId"
            />
            {/** ====================================================== */}
            <AsyncRoute
              loading={this.getLoadingComponent}
              path="/:eventSlug/contact"
              getComponent={this.getContact}
            />
            <AsyncRoute
              loading={this.getLoadingComponent}
              path="/:eventSlug/accessibility"
              getComponent={this.getAccessibility}
            />
            <AsyncRoute
              loading={this.getLoadingComponent}
              path="/:eventSlug/seatingmap/:orderId"
              getComponent={this.getSeatingMap}
            />
            <AsyncRoute
              loading={this.getLoadingComponent}
              path="/:eventSlug/waitlist-pass/:waitlistId"
              getComponent={this.getWaitlistPass}
            />
          </Router>
          {this.isWidget === false ? <Footer path="/:eventSlug" /> : null}
        </main>
      </Provider>
    );
  }
}



// WEBPACK FOOTER //
// ./app.js